<template>

    <ui-input
        :modelValue="inputValue" :readonly="!inputEnabled" :clickable="!inputEnabled"
        :error="error" :disabled="disabled" :prefix="prefix" :suffix="suffix" :icon="icon" :ui-icon="uiIcon"
        :cleave="cleaveOptions" ref="inputRef" v-bind="passedAttrs"
        @update:modelValue="onInput" @focus="onFocus" @blur="onBlur" @change="onChange" @click.stop></ui-input>

</template>

<script lang="ts">

import { Component, Prop, Ref, Vue, Watch } from 'vue-facing-decorator';

import { DateTime } from 'luxon';
import { first, omit } from 'lodash';

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin, uuid } from '@wittignl/js-core';

import popupMixin from '../../mixin/popup';

import type { InputComp } from './Input.vue';
import Input from './Input.vue';

import DatePickerPopup from '../popup/DatePickerPopup.vue';

const CLEAVE_TOKEN_MAP = {
    'dd': 'd',
    'd': 'd',
    'LL': 'm',
    'L': 'm',
    'yyyy': 'Y',
    'yy': 'y'
};

const CLEAVE_LENGTH_MAP = {
    'dd': 2,
    'd': 1,
    'LL': 2,
    'L': 1,
    'yyyy': 4,
    'yy': 2
};

@Component({
    name: 'ui-date-input',
    components: {
        'ui-input': Input
    },
    mixins: [popupMixin],
    emits: ['update:modelValue', 'change', 'navigate']
})
export default class DateInput extends Vue {

    @Prop({ type: [String, Array] })
    modelValue: string | string[];

    @Prop({ type: [String, Boolean, Array] })
    error: string | boolean | string[];

    @Prop({ type: Boolean })
    disabled: boolean;

    @Prop({ type: String })
    prefix: string;

    @Prop({ type: String })
    suffix: string;

    @Prop({ type: String })
    icon: string;

    @Prop({ type: String })
    uiIcon: string;

    @Prop({ type: Boolean })
    time: boolean;

    @Prop({ type: Boolean, default: true })
    picker: boolean;

    @Prop({ type: Boolean, default: true })
    input: boolean;

    @Prop({ type: String })
    valueFormat: string;

    @Prop({ type: String })
    format: string;

    @Prop({ type: String })
    minDate: string;

    @Prop({ type: String })
    maxDate: string;

    @Prop({ type: Boolean })
    period: boolean;

    @Prop({ type: Number })
    minPeriod: number;

    @Prop({ type: Number })
    maxPeriod: number;

    @Prop({ type: Boolean })
    loading: boolean;

    @Prop({ type: Function })
    validateDate: Function

    @Ref
    inputRef: InputComp;

    selectedDate: string = null;
    openPickerId: string;

    created() {

        this.selectedDate = this.modelValue || null;
    }

    @Watch('modelValue')
    onModelValueUpdate(value) {

        this.selectedDate = value;
    }

    get inputValue() {

        if(!this.selectedDate){
            return null;
        }

        if (this.period) {

            const selectedStartDate = this.selectedDate[0];
            const selectedEndDate = this.selectedDate[1];

            let formattedValues = [];
            if (selectedStartDate) {
                const startDate = this.valueFormat ? DateTime.fromFormat(selectedStartDate, this.valueFormat) : DateTime.fromISO(selectedStartDate);
                formattedValues.push(startDate.toFormat(this.resolvedFormat));
            }
            if(selectedEndDate){
                const endDate = this.valueFormat ? DateTime.fromFormat(selectedEndDate, this.valueFormat) : DateTime.fromISO(selectedEndDate);
                formattedValues.push(endDate.toFormat(this.resolvedFormat));
            }

            return formattedValues.length > 0 ? formattedValues.join(" - ") : null;
        }
        else {

            const date = this.valueFormat ? DateTime.fromFormat(this.selectedDate, this.valueFormat) : DateTime.fromISO(this.selectedDate);
            return date ? date.toFormat(this.resolvedFormat) : null;
        }
    }

    get resolvedFormat() {

        if(this.format){
            return this.format;
        }

        if(this.inputEnabled){

            const dateFormat = 'dd-LL-yyyy';
            return this.time ? `${dateFormat} HH:mm` : dateFormat;
        }

        return this.time ? 'ff' : 'DDD';
    }

    get cleaveOptions() {

        if(!this.inputEnabled){
            return null;
        }

        const dateFormatParts = first(this.resolvedFormat.split(' ')).split(this.localeDateSeparator);

        if(this.time){

            const dateLengths = dateFormatParts.map(part => CLEAVE_LENGTH_MAP[part]);

            return {
                delimiters: [this.localeDateSeparator, this.localeDateSeparator, ' ', ':'],
                blocks: [...dateLengths, 2, 2],
                numericOnly: true
            };
        }
        else {

            const pattern = dateFormatParts.map(part => CLEAVE_TOKEN_MAP[part]);

            return {
                date: true,
                delimiter: this.localeDateSeparator,
                datePattern: pattern
            };
        }
    }

    get localeDateSeparator() {

        return '-';

        // TODO: Default resolvedFormat doesn't use localized pattern, so always return - for now
        // const date = DateTime.now().toFormat('D');
        //
        // return (
        //     date.indexOf('/') > -1 ? '/' : (
        //         date.indexOf('-') > -1 ? '-' : '.'
        //     )
        // );
    }

    get inputEnabled() {

        return this.input && !this.period;
    }

    updateValue(value) {

        this.selectedDate = value;
        this.$emit('update:modelValue', value);
    }

    onInput(value) {

        if(!this.inputEnabled){
            return;
        }

        let dateObject = null;

        if(value){

            dateObject = this.time ? DateTime.fromFormat(value, this.resolvedFormat) : DateTime.fromISO(value);

            if(!dateObject.isValid){
                return;
            }
        }

        let dateValue = null;

        if(dateObject){

            if(this.valueFormat){
                dateValue = dateObject.toFormat(this.valueFormat)
            }
            else if(this.time){
                dateValue = dateObject.toISO();
            }
            else {
                dateValue = dateObject.toISODate();
            }
        }

        if(dateValue){
            this.updateValue(dateValue);
        }
    }

    onFocus() {

        if(this.picker){
            this.showPicker();
        }
    }

    onBlur(e) {

        if (!e.relatedTarget || this.elementIsInPicker(e.relatedTarget)) {
            e.stopImmediatePropagation();
            e.preventDefault();
            return false;
        }

        this.updateInputValue();
        this.hidePicker();
    }

    onChange(val, e=null){

        e?.stopPropagation();
        this.$emit('change', this.selectedDate, e);
    }

    updateInputValue() {

        this.inputRef.updateFromValue(this.inputValue);
    }

    commitInput(value) {

        this.updateValue(value);
        this.updateInputValue();
    }

    async showPicker() {

        if(this.openPickerId){
            return;
        }

        this.openPickerId = `ui-date-input-popup-${uuid()}`;

        const oldSelectedDate = this.selectedDate;

        await this.$popup(DatePickerPopup, {

            anchor: this.$el,
            placement: 'bottom|top|right|left'

        }, () => (
            {
                time: this.time,
                valueFormat: this.valueFormat,
                minDate: this.minDate,
                maxDate: this.maxDate,
                validateDate: this.validateDate,
                modelValue: this.selectedDate,
                period: this.period,
                minPeriod: this.minPeriod,
                maxPeriod: this.maxPeriod,
                loading: this.loading
            }
        ), {
            id: this.openPickerId,
            on: {
                'update:modelValue': this.commitInput.bind(this),
                'navigate': period => this.$emit('navigate', period)
            }
        });

        this.inputRef.blurInput();

        if(oldSelectedDate !== this.selectedDate){
            this.onChange(this.selectedDate);
        }

        this.openPickerId = null;
    }

    hidePicker() {

        if(this.openPickerId){

            this.$overlay.close(this.openPickerId);
            this.openPickerId = null;
        }
    }

    elementIsInPicker(element) {

        const $picker = this.openPickerId ? this.$overlay.findOverlay(this.openPickerId)?.$overlayRef()?.$el : null;
        return $picker && element && ($picker === element || $picker.contains(element));
    }

    beforeUnmount() {

        this.hidePicker();
    }

    get passedAttrs() {

        return omit(this.$attrs, ['modelValue', 'onUpdate:modelValue']);
    }
}

export type DateInputComp = InstanceType<typeof DateInput>
export type DateInputProps = ComponentProps<DateInputComp>
export const dateInputCompMixin = createComponentMixin(DateInput)

</script>